import { useState } from "react";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../../shared/bootstrap/Toast";
import { PaymentGatewayTransaction } from "../../../entities/company/PaymentProvider/PaymentGatewayTransaction";
import PaymentGatewayTransactionService from "../../../services/PaymentGatewayTransactionService";
import { PaymentLinkTransactionLogResponse } from "../../../entities/company/PaymentProvider/PaymentLinkTransactionLog";
import Loading from "../../shared/components/Loading";

const copyText = (text: string) => {
  navigator.clipboard.writeText(text);
  ToastService.showToast(
    TranslationService.translate.CopiedToClipboard,
    undefined,
    "success"
  );
};

const PaymentGatewayTransactionLogDetail = ({
  transaction,
  onClose
}: {
  transaction: PaymentGatewayTransaction;
  onClose?: () => void;
}) => {
  // Estados para el log de webhook
  const [webhookLogLoading, setWebhookLogLoading] = useState(false);
  const [webhookLogData, setWebhookLogData] =
    useState<PaymentLinkTransactionLogResponse | null>(null);
  const [showWebhookLog, setShowWebhookLog] = useState(false);
  const [hasNoWebhookLogs, setHasNoWebhookLogs] = useState(false);

  // Parsear los JSON strings
  let requestParsed: any = null;
  let responseParsed: any = null;
  let invoicesParsed: any[] = [];

  try {
    requestParsed = JSON.parse(transaction.Request);
  } catch (e) {
    requestParsed = transaction.Request;
  }

  try {
    responseParsed = JSON.parse(transaction.Response);
  } catch (e) {
    responseParsed = transaction.Response;
  }

  try {
    invoicesParsed = JSON.parse(transaction.Invoices);
  } catch (e) {
    invoicesParsed = [];
  }

  // Función para cargar el log de webhook
  const loadWebhookLog = async () => {
    if (webhookLogData || hasNoWebhookLogs) {
      // Si ya está cargado o ya sabemos que no hay logs, solo mostrar/ocultar
      setShowWebhookLog(!showWebhookLog);
      return;
    }

    setShowWebhookLog(true);
    setWebhookLogLoading(true);
    const result =
      await PaymentGatewayTransactionService.getPaymentLinkTransactionLog(
        transaction.PaymentLinkTransactionId
      );

    if (result instanceof Error) {
      ToastService.showToast(
        "Error al cargar el log de webhook",
        undefined,
        "danger"
      );
      setWebhookLogLoading(false);
      setShowWebhookLog(false);
      return;
    }

    // Verificar si la respuesta es null o si el log es null
    if (!result || !result.log || !result.log.WebhookData) {
      setHasNoWebhookLogs(true);
      setWebhookLogData(null);
    } else {
      setWebhookLogData(result);
      setHasNoWebhookLogs(false);
    }
    setWebhookLogLoading(false);
  };

  return (
    <div className="floatingBody p-4">
      <div className="ps-3 d-flex h-100 flex-column">
        <MiniTable
          data={[
            {
              title: "ID de Transacción",
              value: transaction.PaymentLinkTransactionId.toString()
            },
            {
              title: "Fecha y hora",
              value: transaction.Created
                ? new Date(transaction.Created).toLocaleString()
                : ""
            },
            {
              title: "Monto",
              value:
                transaction.Amount?.toLocaleString("es-MX", {
                  style: "currency",
                  currency: "MXN"
                }) || transaction.Amount.toString()
            },
            {
              title: "Exitoso",
              value: () =>
                transaction.IsSuccessful ? (
                  <span className="badge badge-success">Sí</span>
                ) : (
                  <span className="badge badge-danger">No</span>
                )
            },
            {
              title: "Facturas procesadas",
              value: transaction.NumberInvoicesProcess.toString()
            },
            {
              title: "Estado de pago",
              value: transaction.PaymentSatusDescription
            },
            {
              title: "Proveedor",
              value: transaction.PaymentLinkProviderDescription
            },
            { title: "ID de Usuario", value: transaction.UserId.toString() },
            {
              title: "ID Cliente Externo",
              value: transaction.ExternalCLientId || "N/A"
            },
            {
              title: "Link de Checkout",
              value: () =>
                transaction.CheckoutLink ? (
                  <a
                    href={transaction.CheckoutLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {transaction.CheckoutLink}
                  </a>
                ) : (
                  <span>N/A</span>
                )
            }
          ]}
        />
        {invoicesParsed.length > 0 && (
          <>
            <hr className="w-100" />
            <div className="mb-3">
              <label>
                <b>Facturas</b>
              </label>
              <div
                className="border rounded p-2"
                style={{ maxHeight: 200, overflowY: "auto" }}
              >
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>ID Factura</th>
                      <th>ID Cliente</th>
                      <th>Monto</th>
                      <th>Número de Referencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoicesParsed.map((inv: any, idx: number) => (
                      <tr key={idx}>
                        <td>{inv.ioId}</td>
                        <td>{inv.personId}</td>
                        <td>
                          {inv.amount?.toLocaleString("es-MX", {
                            style: "currency",
                            currency: "MXN"
                          }) || inv.amount}
                        </td>
                        <td>{inv.referenceNumber}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        <hr className="w-100" />
        <div className="mb-2">
          <label
            className="d-flex align-items-center mb-2"
            style={{ cursor: "pointer" }}
            onClick={loadWebhookLog}
          >
            <b className="is-link">
              {TranslationService.translate.ViewWebhookLogs}
            </b>
            {webhookLogLoading && (
              <i className="fas fa-spinner fa-pulse px-2"></i>
            )}
          </label>
          {showWebhookLog && (
            <>
              {webhookLogLoading ? (
                <div style={{ minHeight: 100 }}>
                  <Loading height={100} />
                </div>
              ) : hasNoWebhookLogs ? (
                <div className="text-muted mt-2">
                  {TranslationService.translate.NoWebhookLogs}
                </div>
              ) : (
                webhookLogData && (
                  <>
                    <label className="d-flex align-items-center mb-1">
                      <b>Webhook Data JSON</b>
                      <i
                        className="fal fa-copy is-link px-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => copyText(webhookLogData.log.WebhookData)}
                      />
                    </label>
                    <div
                      style={{
                        background: "#f8f8f8",
                        padding: 8,
                        borderRadius: 4,
                        maxHeight: 300,
                        maxWidth: "100%",
                        overflow: "auto"
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {webhookLogData.log.WebhookData}
                      </pre>
                    </div>
                  </>
                )
              )}
            </>
          )}
        </div>
        <hr className="w-100" />
        <div className="row">
          <div className="col-6">
            <label className="d-flex align-items-center">
              <b>Request JSON</b>
              <i
                className="fal fa-copy is-link px-2"
                style={{ cursor: "pointer" }}
                onClick={() => copyText(JSON.stringify(requestParsed, null, 2))}
              />
            </label>
            <div
              style={{
                background: "#f8f8f8",
                padding: 8,
                borderRadius: 4,
                maxHeight: 300,
                maxWidth: "100%",
                overflow: "auto"
              }}
            >
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(requestParsed, null, 2)}
              </pre>
            </div>
          </div>
          <div className="col-6">
            <label className="d-flex align-items-center">
              <b>Response JSON</b>
              <i
                className="fal fa-copy is-link px-2"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  copyText(JSON.stringify(responseParsed, null, 2))
                }
              />
            </label>
            <div
              style={{
                background: "#f8f8f8",
                padding: 8,
                borderRadius: 4,
                maxHeight: 300,
                maxWidth: "100%",
                overflow: "auto"
              }}
            >
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(responseParsed, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
      <FloatingPanelFooter>
        {onClose && (
          <button className="btn btn-secondary mt-2" onClick={onClose}>
            {TranslationService.translate.Close}
          </button>
        )}
      </FloatingPanelFooter>
    </div>
  );
};

type MiniTableData = { title?: string; value?: string | (() => JSX.Element) };

const MiniTable = ({ data }: { data: MiniTableData[] }) => (
  <div className="row">
    {data.map((x, i) => (
      <div key={x.title || i} className="col-6 mb-1">
        <div className="row">
          {x.title ? (
            <div className="col-4">
              <label>{x.title}</label>
            </div>
          ) : null}
          <div className="col-8">
            {x.value && (typeof x.value === "string" ? x.value : x.value())}
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default PaymentGatewayTransactionLogDetail;
